import { Header, Icon, Popup } from 'semantic-ui-react';
import ReactGA from 'react-ga4';

import * as S from './styles';
import DocumentTypeSidebar from './components/DocumentTypeSidebar';
import DocumentList from './components/DocumentList';
import MainContentContainer from './components/MainContentContainer';
import PageLoader from '~/components/PageLoader';
import AdditionalDocumentsUploadButton from './components/AdditionalDocumentsUploadButton';
import PortfolioProjectSwitcher from '~/components/PortfolioProjectSwitcher';
import DataroomActions from './components/DataroomActions';
import { useDataroomContext } from './providers/DataroomProvider';
import { usePageHeaderContext } from '~/components/PageHeader/PageHeaderProvider';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { usePageType, usePortfolioContext, useProjectContext } from '~/hooks';
import { ProjectStatus } from '~/constants';
import { Project } from '~/types/project';

export default function Dataroom() {

  const pageHeaderContext = usePageHeaderContext();
  const { isLoading, hasError } = useDataroomContext();
  const { isSimplifiedUI } = useDataroomContext();
  const { permissions } = usePermissions();
  const pageType = usePageType();
  const { project } = useProjectContext();
  const { portfolio } = usePortfolioContext();

  if (portfolio) {
    ReactGA.send({
      hitType: 'pageview',
      page: '/portfolio/id/dataroom',
      title: `${portfolio?.name} | Conductor Solar`
    });
    document.title = `${portfolio?.name} | Conductor Solar`;
  } else {
    ReactGA.send({
      hitType: 'pageview',
      page: '/project/id/dataroom',
      title: `${project?.name} | Conductor Solar`
    });
    document.title = `${project?.name} | Conductor Solar`;
  }


  const isSimplifedEmptyState = pageType === 'project'
    ? project?.pricingType === 'ITC' || project?.status === ProjectStatus.MARKET_QUOTE
    : portfolio?.projects?.every((p: Project) => p.pricingType === 'ITC');

  if (isLoading) {
    return (
      <div style={{ position: 'relative', width: '100%', height: pageHeaderContext.height ? `calc(100vh - ${pageHeaderContext.height / 16}rem)` : '100%' }}>
        <PageLoader />
      </div>
    );
  } else if (hasError) {
    return null;
  }

  return (
    isLoading ? (
      <div style={{ position: 'relative', width: '100%', height: pageHeaderContext.height ? `calc(100vh - ${pageHeaderContext.height / 16}rem)` : '100%' }}>
        <PageLoader />
      </div>
    ) : (
      <>
        {hasError ? (
          <div>Error loading data room</div>
        ) : (

          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--medium)', paddingTop: '4px', paddingBottom: 'var(--medium)', borderBottom: '1px solid var(--color-grey)', marginLeft: '-24px', marginRight: '-24px', paddingLeft: '24px', paddingRight: '24px' }}>
              <Header as="h2" style={{ marginBottom: '0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {pageType === 'project' ? 'Project Data Room' : 'Portfolio Data Room'}
                {isSimplifiedUI && permissions.canUpdateDataroom && !isSimplifedEmptyState && (
                  <Popup
                    position="bottom center"
                    hoverable
                    inverted
                    trigger={<Icon name="info circle" color="blue" style={{ cursor: 'pointer', fontSize: '1rem', margin: 0, marginTop: '-12px' }} />}
                    content={(
                      <span>
                        Based on the type of {pageType} you’re building, Conductor Solar recommends sharing the following documents with potential partners.
                        You can drag-and-drop the files directly onto each row or click to upload files. If you have other documents you’d like to share,
                        simply click the ‘add more documents’ button at the bottom,
                        or drag-and-drop as many files as you like directly onto the ‘additional files’ section.
                      </span>
                    )}
                  />
                )}
                {isSimplifiedUI && !permissions.canUpdateDataroom && pageType === 'project' && (
                  <Popup
                    position="bottom center"
                    hoverable
                    inverted
                    trigger={<Icon name="info circle" color="blue" style={{ cursor: 'pointer', fontSize: '1rem', margin: 0, marginTop: '-12px' }} />}
                    content={(
                      <span>
                        {'Below are the project-level documents shared at this stage.' + (project?.portfolioId ? ' Make sure to check the other data rooms in this portfolio for additional documents.' : '')}
                      </span>
                    )}
                  />
                )}
              </Header>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--x-small)' }}>
                <PortfolioProjectSwitcher />
                {isSimplifiedUI && <DataroomActions usePortfolioRoot={pageType === 'portfolio'} />}
              </div>
            </div>
          <S.Container pageHeaderContext={pageHeaderContext}>
            <DocumentTypeSidebar />
            <MainContentContainer>

              {(isSimplifiedUI && permissions.canUpdateDataroom && isSimplifedEmptyState) && (
                <p style={{ marginBottom: 'var(--medium)' }}>
                  If you have documents you’d like to share, simply click the ‘add more documents’ button at the bottom,
                  or drag-and-drop as many files as you like directly onto the ‘additional files’ section.
                </p>
              )}

              <DocumentList />

              {isSimplifiedUI && (
                <div style={{ marginTop: 'calc(var(--medium) - var(--2x-small))' }}>
                  <AdditionalDocumentsUploadButton />
                </div>
              )}
            </MainContentContainer>
          </S.Container>
          </>
        )}
      </>
    )

  );
}

